import type { z } from 'zod'
import { ApiErrorSchema } from '@shared/schema/api'

/**
 * ตัวเรียก API ตัวเดียวของทั้งแอป
 * ทุก response ถูก parse ด้วย zod schema เดียวกับที่ฝั่งเซิร์ฟเวอร์ใช้
 * → ถ้าสัญญาระหว่างสองฝั่งเพี้ยน จะพังทันทีตอนพัฒนา/เทส ไม่ใช่เงียบ ๆ ตอนพนักงานใช้งานจริง
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  /** ข้อผิดพลาดจากเครือข่าย (ควรลองใหม่ภายหลัง) */
  get isNetwork(): boolean {
    return this.status === 0
  }
}

type RequestOptions<T extends z.ZodType | undefined> = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  schema?: T
  signal?: AbortSignal
  /** ส่ง FormData แทน JSON (ใช้ตอนอัปโหลดรูป) */
  formData?: FormData
}

export async function apiRequest<T extends z.ZodType>(
  path: string,
  options: RequestOptions<T> & { schema: T },
): Promise<z.infer<T>>
export async function apiRequest(path: string, options?: RequestOptions<undefined>): Promise<unknown>
export async function apiRequest(
  path: string,
  options: RequestOptions<z.ZodType | undefined> = {},
): Promise<unknown> {
  const { method = 'GET', body, schema, signal, formData } = options

  let response: Response
  try {
    response = await fetch(path, {
      method,
      signal: signal ?? null,
      credentials: 'same-origin',
      headers: formData ? undefined : body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'network_error', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาตรวจสอบสัญญาณอินเทอร์เน็ต')
  }

  if (response.status === 204) return null

  const text = await response.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      throw new ApiError(response.status, 'invalid_response', 'เซิร์ฟเวอร์ตอบกลับในรูปแบบที่อ่านไม่ได้')
    }
  }

  if (!response.ok) {
    const parsed = ApiErrorSchema.safeParse(payload)
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      )
    }
    throw new ApiError(response.status, 'unknown_error', `เกิดข้อผิดพลาด (${response.status})`)
  }

  if (!schema) return payload

  const parsed = schema.safeParse(payload)
  if (!parsed.success) {
    console.error('response ไม่ตรงกับ schema', path, parsed.error)
    throw new ApiError(response.status, 'schema_mismatch', 'ข้อมูลที่ได้รับจากเซิร์ฟเวอร์ไม่ถูกต้อง')
  }
  return parsed.data
}

export const api = {
  get: <T extends z.ZodType>(path: string, schema: T, signal?: AbortSignal) =>
    apiRequest(path, { schema, signal }),
  post: <T extends z.ZodType>(path: string, body: unknown, schema: T) =>
    apiRequest(path, { method: 'POST', body, schema }),
  put: <T extends z.ZodType>(path: string, body: unknown, schema: T) =>
    apiRequest(path, { method: 'PUT', body, schema }),
  patch: <T extends z.ZodType>(path: string, body: unknown, schema: T) =>
    apiRequest(path, { method: 'PATCH', body, schema }),
  del: <T extends z.ZodType>(path: string, body: unknown, schema: T) =>
    apiRequest(path, { method: 'DELETE', body, schema }),
  send: (path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown) =>
    apiRequest(path, { method, body }),
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ'
}
